import { For, Index } from "solid-js"
import { useLanguage } from "@/context/language"
import { Bar, Skeleton, skeletonTiming } from "./skeleton"
import type { FilesScope } from "./source-files"
import { TablerIcon } from "./tabler-icons"
import "./tokens.css"
import "./source-files.css"

// Source files while it loads (DESIGN-LOG §63), in the page's own boxes: the column at the URL's level (the clients,
// All clients and the clients with their counts; or one scope's reports, a name and two lines each), and the picked
// report's period: the head, the dropzone and the files. Same classes as
// source-files-page.tsx and drill-column.tsx draw, so every height and gap is the page's.

type T = (key: string) => string
const useT = (): T => {
  const language = useLanguage()
  return (key) => language.t(key as Parameters<typeof language.t>[0])
}

// Level 1 under its title and search: the legend, All clients, the clients and their counts.
export function ClientRowsSkeleton() {
  return (
    <Skeleton class="dx-skel-stack">
      <p class="dx-sf-legend"><Bar w={48} /><Bar w={12} /></p>
      <div class="dx-sf-list">
        <div class="dx-sf-opt" data-edge="first"><span class="dx-sf-lbl"><Bar w="46%" /></span></div>
        <For each={[62, 54, 70, 48, 66, 58, 74]}>{(width) => (
          <div class="dx-sf-opt"><span class="dx-sf-lbl"><Bar w={`${width}%`} /></span><Bar w={8} /></div>
        )}</For>
      </div>
    </Skeleton>
  )
}

// Level 2 under the scope's name: a report's name, whom it feeds and what its latest period holds (Workflows: the name
// and its one line, `lines` 1).
export function ReportRowsSkeleton(props: { lines?: number }) {
  return (
    <Skeleton class="dx-sf-list">
      <For each={[66, 52, 58]}>{(width) => (
        <div class="dx-sf-opt dx-sf-rep">
          <span class="dx-sf-lbl">
            <span class="dx-sf-nm"><Bar w={`${width}%`} /></span>
            <For each={[58, 44].slice(0, props.lines ?? 2)}>{(line) => <span class="dx-sf-hint"><Bar w={`${line}%`} /></span>}</For>
          </span>
        </div>
      )}</For>
    </Skeleton>
  )
}

// The column before the page's code and first read are in, at the URL's level (`drill`, source-files.ts drillScope):
// the title, the search and the clients; or the way back, the scope's name (a client's a bar until the factsheets say
// it) and its reports, `lines` each under the name. `link`: Source files' All files beside the title.
export function ColumnSkeleton(props: { title: string; link?: boolean; drill?: FilesScope["kind"]; lines?: number }) {
  const t = useT()
  return (
    <aside class="dx-sf-col">
      <div class="dx-sf-lvl">
        {props.drill ? (
          <>
            <div class="dx-sf-col-head"><span class="dx-sf-col-back"><TablerIcon name="chevron-left" size={16} stroke-width="1.6" />{t("datox.v2.files.col.clients")}</span></div>
            <h2 class="dx-sf-col-scope">{props.drill === "client" ? <Bar w="56%" /> : t(props.drill === "none" ? "datox.v2.home.notClient" : "datox.v2.files.col.all")}</h2>
            <ReportRowsSkeleton lines={props.lines} />
          </>
        ) : (
          <>
            <div class="dx-sf-col-head"><h1>{props.title}</h1>{props.link && <Bar w={44} />}</div>
            <span class="dx-sf-col-search"><TablerIcon name="search" size={16} stroke-width="1.6" /></span>
            <ClientRowsSkeleton />
          </>
        )}
      </div>
    </aside>
  )
}

// File rows in a files list: the icon, the name and what it holds, when it came. A list's own <li>s, so the list stays
// a list; the list itself is the busy region.
export function FileRowsSkeleton(props: { label?: string; rows?: number }) {
  const timing = skeletonTiming()
  return (
    <>
      <Index each={[58, 42, 50, 46, 62, 38].slice(0, props.rows ?? 3)}>{(width) => (
        <li class="dx-sf-f dx-sf-ghost dx-skel" style={timing}>
          <Bar round />
          <div class="dx-sf-body"><div class="dx-sf-name"><Bar w={`${width()}%`} /></div><div class="dx-sf-sub"><Bar w={`${width() - 14}%`} /></div></div>
          <Bar w={108} />
        </li>
      )}</Index>
      {props.label && <li class="sr-only" role="status">{props.label}</li>}
    </>
  )
}

// The picked report's period: the head (the title, its line of facts), the dropzone as it first draws (calm: the
// files are not in yet) and the files.
export function SetViewSkeleton(props: { label?: string }) {
  return (
    <Skeleton class="dx-sf-view" label={props.label}>
      <div class="dx-sf-head"><div class="dx-sf-t"><h2 class="dx-sf-title"><Bar w={140} /></h2><p class="dx-sf-facts"><Bar w={320} /></p></div></div>
      <div class="dx-sf-zone" data-calm>
        <div class="dx-sf-zone-main">
          <span class="dx-sf-zone-icon"><TablerIcon name="upload" size={16} stroke-width="1.6" /></span>
          <span class="dx-sf-zone-title"><Bar w={150} /></span>
        </div>
      </div>
      <div class="dx-sf-card"><ul class="dx-sf-files"><FileRowsSkeleton /></ul></div>
    </Skeleton>
  )
}

// All files: the list under the search, its own rows.
export function AllFilesSkeleton(props: { label?: string }) {
  return <ul class="dx-sf-set dx-sf-files" aria-busy="true"><FileRowsSkeleton label={props.label} rows={6} /></ul>
}

// The whole page, before its code and its first read are in: the column at the URL's level (`drill`) and the picked
// report's period; or All files (`all`).
export function SourceFilesSkeleton(props: { all?: boolean; drill?: FilesScope["kind"] }) {
  const t = useT()
  return (
    <Skeleton class="dx-v2 dx-sf" data-view={props.all ? "all" : "nav"}>
      {props.all ? (
        <div class="dx-sf-page">
          <div class="dx-sf-bar dx-sf-bar-all">
            <span class="dx-sf-link dx-sf-back"><TablerIcon name="arrow-left" size={14} stroke-width="1.6" />{t("datox.v2.files.title")}</span>
            <h1>{t("datox.v2.files.all.title")}</h1>
          </div>
          <div class="dx-sf-all">
            <span class="dx-sf-search"><TablerIcon name="search" size={16} stroke-width="1.6" /></span>
            <AllFilesSkeleton label={t("datox.v2.files.loadingFiles")} />
          </div>
        </div>
      ) : (
        <>
          <ColumnSkeleton title={t("datox.v2.files.title")} link drill={props.drill} />
          <div class="dx-sf-main"><div class="dx-sf-page"><SetViewSkeleton label={t("datox.v2.files.loadingPage")} /></div></div>
        </>
      )}
    </Skeleton>
  )
}
